import { API } from '@/config/api';
import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminNavigation from '@/components/AdminNavigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowLeft, Calendar, MapPin, Trash2, RotateCcw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

// Same organism list as AdminBulkView
const ALL_ORGANISMS = [
  { group: 'Kiselalger', species: 'Skeletonema', depth: 3 },
  { group: 'Kiselalger', species: 'Chaetoceros', depth: 3 },
  { group: 'Kiselalger', species: 'Pseudo-Nitzschia', depth: 3 },
  { group: 'Kiselalger', species: 'Div', depth: 3 },
  { group: 'Kiselalger', species: 'KISELALGER SUM', depth: '', isSum: true },
  { group: 'Svepeflagellater', species: 'Chryso/Prymn', depth: 5 },
  { group: 'Svepeflagellater', species: 'Phaeocystis', depth: 5 },
  { group: 'Nåleflagellater', species: 'Pseudochatonella', depth: 3 },
  { group: 'Kiselflagellater', species: 'Dictyocha/Vicicitus', depth: 5 },
  { group: 'Fureflagellater', species: 'Gymnodinium/Karenia/Kalodinium', depth: 5 },
  { group: 'Fureflagellater', species: 'Alexandrium', depth: 5 },
];

const ALL_ZOO_ORGANISMS = [
  { sampleType: 'Håvtrekk', organism: 'Mikromaneter' },
  { sampleType: 'Håvtrekk', organism: 'Kolonimaneter' },
  { sampleType: 'Håvtrekk', organism: 'Små maneter' },
  { sampleType: 'Håvtrekk', organism: 'Radiolaria' },
  { sampleType: 'Håvtrekk', organism: 'Pigghudlarver' },
  { sampleType: 'Håvtrekk', organism: 'Foraminifera' },
  { sampleType: 'Håvtrekk', organism: 'Hydroider' },
  { sampleType: 'Håvtrekk', organism: 'Larver skjell' },
  { sampleType: 'Håvtrekk', organism: 'Ribbemanet' },
  { sampleType: 'Visuelt', organism: 'Manet' },
];

function getShortName(loc) {
  const m = loc.match(/- (.+)$/);
  return m ? m[1] : loc;
}

export default function DeletedDraftView() {
  const { draftId } = useParams();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [draftRes, locsRes] = await Promise.all([
          axios.get(`${API}/deleted-drafts/${draftId}`, { withCredentials: true }),
          axios.get(`${API}/locations`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        const d = draftRes.data;
        setDraft(d);

        // Determine locations: from area config or from data
        const areaId = d.selected_area;
        const areaLocs = (locsRes.data || []).filter(l => l.area_id === areaId);

        if (areaLocs.length > 0) {
          // Use area's configured locations
          setLocations(areaLocs.map(l => `${l.letter} - ${l.name}`));
        } else {
          // Fallback: extract from draft data
          const gridData = d.grid_data || {};
          const firstOrg = Object.values(gridData)[0] || {};
          setLocations(Object.keys(firstOrg));
        }
      } catch {
        toast.error('Kunne ikkje hente sletta utkast');
        navigate('/admin/audit-log');
      } finally { setLoading(false); }
    })();
  }, [draftId, navigate]);

  const handleRestore = async () => {
    setRestoring(true);
    try {
      await axios.post(`${API}/deleted-drafts/${draftId}/restore`, {}, { withCredentials: true });
      toast.success('Utkast gjenoppretta');
      navigate('/admin/bulk-history');
    } catch { toast.error('Kunne ikkje gjenopprette'); }
    finally { setRestoring(false); }
  };

  if (loading) {
    return (
      <>
        <AdminNavigation />
        <div className="min-h-screen bg-white lg:pl-[240px] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </>
    );
  }

  if (!draft) return null;

  const algaeData = draft.grid_data || {};
  const zooData = draft.zooplankton_grid_data || {};
  const colorOverrides = draft.color_overrides || {};
  const tempData = draft.temperature_data || {};

  const COLOR_MAP = {
    green: 'bg-emerald-100 text-emerald-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    orange: 'bg-orange-100 text-orange-800',
    red: 'bg-red-100 text-red-800',
  };

  return (
    <>
      <AdminNavigation />
      <div className="min-h-screen bg-white lg:pl-[240px]">
        <div className="max-w-full mx-auto px-6 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-4">
              <Link to="/admin/audit-log">
                <Button variant="ghost"><ArrowLeft className="w-4 h-4 mr-1" /> Tilbake til endringslogg</Button>
              </Link>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-slate-900">Sletta utkast</h1>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200 flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Sletta
                  </span>
                </div>
                <div className="flex items-center space-x-4 text-slate-600 text-sm">
                  {draft.sample_date && <div className="flex items-center space-x-1"><Calendar className="w-4 h-4" /><span>{draft.sample_date}</span></div>}
                  {draft.selected_area && <div className="flex items-center space-x-1"><MapPin className="w-4 h-4" /><span>{draft.selected_area}</span></div>}
                  {draft.deleted_by_email && <span>Sletta av: {draft.deleted_by_email}</span>}
                  {draft.deleted_at && <span>{new Date(draft.deleted_at).toLocaleString('nb-NO')}</span>}
                </div>
              </div>
              <Button onClick={handleRestore} disabled={restoring} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="restore-draft-btn">
                {restoring ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                Gjenopprett utkast
              </Button>
            </div>
          </div>

          {/* Warning */}
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800 text-sm">Dette utkastet er sletta</p>
              <p className="text-xs text-amber-600 mt-0.5">Klikk "Gjenopprett utkast" for å flytte det tilbake til aktive utkast.</p>
            </div>
          </div>

          {/* Algae Table - full schema */}
          <Card className="shadow-xl border-0 mb-8">
            <CardHeader><CardTitle className="text-2xl">Algeresultater</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="border border-slate-300 px-4 py-3 text-left font-bold">Orden/gruppe</th>
                      <th className="border border-slate-300 px-4 py-3 text-left font-bold">Evt. art</th>
                      <th className="border border-slate-300 px-3 py-3 text-center font-bold">Djup (m)</th>
                      {locations.map(loc => {
                        const temp = tempData[loc];
                        return (
                          <th key={loc} className="border border-slate-300 px-3 py-2 text-center font-bold min-w-24">
                            <div className="text-sm">{getShortName(loc)}</div>
                            <div className="text-xs text-slate-500">(ant/l)</div>
                            {temp && <div className="mt-0.5 text-[10px] font-semibold text-sky-600 bg-sky-50 rounded-full px-2 py-0.5 inline-block">{temp}°C</div>}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {ALL_ORGANISMS.map(org => {
                      const key = `${org.group}_${org.species}`;
                      return (
                        <tr key={key} className={org.isSum ? 'bg-slate-100 font-semibold' : ''}>
                          <td className="border border-slate-300 px-4 py-3">{org.group}</td>
                          <td className="border border-slate-300 px-4 py-3 italic">{org.isSum ? <strong>{org.species}</strong> : org.species}</td>
                          <td className="border border-slate-300 px-3 py-3 text-center">{org.depth}</td>
                          {locations.map(loc => {
                            const val = algaeData[key]?.[loc] || '';
                            const hasVal = val && String(val).trim() !== '';
                            const overrideKey = `${key}_${loc}`;
                            const color = colorOverrides[overrideKey];
                            const colorClass = color ? COLOR_MAP[color] || '' : '';
                            return (
                              <td key={loc} className="border border-slate-300 p-2">
                                <div className={`h-10 flex items-center justify-center text-sm font-semibold rounded-md ${
                                  org.isSum ? 'bg-slate-200' :
                                  colorClass ? colorClass :
                                  hasVal ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-50 text-slate-400'
                                }`}>
                                  {hasVal ? val : '–'}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Zooplankton Table - full schema */}
          <Card className="shadow-xl border-0 mb-8">
            <CardHeader><CardTitle className="text-2xl">Zooplankton resultater</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="border border-slate-300 px-4 py-3 text-left font-bold">Prøvetype</th>
                      <th className="border border-slate-300 px-4 py-3 text-left font-bold">Organisme</th>
                      {locations.map(loc => (
                        <th key={loc} className="border border-slate-300 px-3 py-2 text-center font-bold min-w-24">
                          <div className="text-sm">{getShortName(loc)}</div>
                          <div className="text-xs text-slate-500">(ant/m³)</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ALL_ZOO_ORGANISMS.map(org => {
                      const key = `${org.sampleType}_${org.organism}`;
                      return (
                        <tr key={key} className="hover:bg-slate-50">
                          <td className="border border-slate-300 px-4 py-3">{org.sampleType}</td>
                          <td className="border border-slate-300 px-4 py-3 italic">{org.organism}</td>
                          {locations.map(loc => {
                            const val = zooData[key]?.[loc] || '';
                            const hasVal = val && String(val).trim() !== '';
                            return (
                              <td key={loc} className="border border-slate-300 p-2">
                                <div className={`h-10 flex items-center justify-center text-sm font-semibold rounded-md ${hasVal ? 'bg-blue-50' : 'bg-slate-50 text-slate-400'}`}>
                                  {hasVal ? val : '–'}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
